import React, { useCallback, useEffect, useRef, useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import { SypacTabs, SypacText } from '@sypac/component-library-react';
import SearchBar from '../../../components/SearchBar/SearchBar';
import { MutatingDots } from 'react-loader-spinner';
import Pagination from '../../../components/Pagination/Pagination';
import { ContractsTable } from '../../../components/ContractsTable/ContractsTable';
import { Sorting } from '../../../components/Sorting/Sorting';
import { SortingItemInterface } from '../../../components/Sorting/Sorting.interface';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LIMIT } from '../../../constants';
import {
	Contract,
	ContractsService,
	GetContractsQueryParams,
} from '../../../services/contracts.service';
import { toastVariant } from '../../../components/ToastVariant/toastVariant';
import SypacToast from '../../../assets/SypacToast';
import { TabsButton } from '../../../components/TabsButton/TabsButton';
import ContractsDetails from '../../../components/ContractsTable/components/ContractsDetails';
import { OrderTarget } from '../../../components/OrdersTable/interfaces/OrderStatus.interface';
import { OrderInterface } from '../../../components/OrdersTable/interfaces/Order.interface';
import { OrdersService } from '../../../services/orders.services';
import OrderDetails from '../OrderDetails';
import { ContractStatus } from '../../../components/ContractsTable/interfaces/Contract.interface';

const sortOptions = [
	{ title: 'Price low to high', value: 'priceLowToHigh' },
	{ title: 'Price high to low', value: 'priceHighToLow' },
	{ title: 'Date low to high', value: 'dateLowToHigh' },
	{ title: 'Date high to low', value: 'dateHighToLow' },
];

const BuyerContracts: React.FC = () => {
	const { t } = useTranslate();
	const location = useLocation();
	const detailsRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	const [contracts, setContracts] = useState<Contract[]>([]);
	const [page, setPage] = useState<number>(0);
	const [count, setCount] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);

	const [statuses, setStatuses] = useState<string[]>([]);
	const [selectedTab, setSelectedTab] = useState('all');
	const [selectedContract, setSelectedContract] = useState<Contract>();
	const [searchQuery, setSearchQuery] = useState<string>();
	const [showDetails, setShowDetails] = useState<boolean>(false);
	const [sorting, setSorting] = useState<SortingItemInterface>();
	const toastScheduledRef = useRef(false);
	const hasConsumedOpenFromStateRef = useRef<string | null>(null);
	const { contractId } = useParams<{ contractId?: string }>();
	const [isOrderOpen, setIsOrderOpen] = useState<boolean>(false);
	const [selectedOrderDetails, setSelectedOrderDetails] = useState<
		OrderInterface | undefined
	>();
	const orderDetailsRef = useRef<HTMLDivElement>(null);

	const sortToApi = useCallback(
		(
			s?: SortingItemInterface,
		):
			| Pick<GetContractsQueryParams, 'orderBy' | 'orderDirection'>
			| undefined => {
			if (!s?.value) return undefined;

			switch (s.value) {
				case 'priceLowToHigh':
					return { orderBy: 'pricePerTonBuyer', orderDirection: 'ASC' };
				case 'priceHighToLow':
					return { orderBy: 'pricePerTonBuyer', orderDirection: 'DESC' };
				case 'dateLowToHigh':
					return { orderBy: 'expiresAt', orderDirection: 'ASC' };
				case 'dateHighToLow':
					return { orderBy: 'expiresAt', orderDirection: 'DESC' };
				default:
					return undefined;
			}
		},
		[],
	);

	const openOrderDetails = useCallback(async (orderId: number) => {
		try {
			const { data } = await OrdersService.getOrder(
				orderId,
				OrderTarget.customer,
			);
			setSelectedOrderDetails(data);
			setIsOrderOpen(true);
		} catch (e) {}
	}, []);

	const closeOrderDetails = useCallback(() => {
		setIsOrderOpen(false);
		setSelectedOrderDetails(undefined);
	}, []);

	const getContractsCallback = useCallback(async () => {
		setLoading(true);
		try {
			const sortParams = sortToApi(sorting);

			const query: GetContractsQueryParams = {
				limit: LIMIT,
				offset: page * LIMIT,
				...sortParams,
				...(statuses.length ? { statuses } : {}),
			};

			const { data } = await ContractsService.getContracts(query, 'customer');

			setContracts(data.items || []);
			setCount(data.count || 0);
		} catch (e) {
		} finally {
			setLoading(false);
		}
	}, [page, sorting, sortToApi, statuses]);

	const setCurrentTab = (statuses: string[]) => {
		setPage(0);
		setStatuses(statuses);
	};

	const handleContractClick = useCallback(
		async (contract: Contract, shouldRefetch = false) => {
			try {
				setIsOrderOpen(false);
				setSelectedOrderDetails(undefined);

				setSelectedContract(undefined);
				const { data } = await ContractsService.getContractById(
					contract.id,
					'customer',
				);
				setSelectedContract(data);
				setShowDetails(true);

				if (shouldRefetch) {
					await getContractsCallback();
				}
			} catch (e) {}
		},
		[getContractsCallback],
	);

	const handleContractUpdate = useCallback(
		async (updated: Contract) => {
			setSelectedContract(updated);
			await getContractsCallback();
		},
		[getContractsCallback],
	);

	const handleSorting = (sort: SortingItemInterface) => {
		setSorting(sort);
		setPage(0);
	};

	const closeModal = () => {
		setSelectedContract(undefined);
		setShowDetails(false);
	};

	const clickOutsideDetails = useCallback(
		(event: MouseEvent) => {
			const target = event.target as Node;

			const isInsideContract = !!detailsRef.current?.contains(target);
			const isInsideOrder = !!orderDetailsRef.current?.contains(target);

			if (isInsideContract || isInsideOrder) {
				return;
			}

			if (isOrderOpen) {
				closeOrderDetails();
			}

			if (showDetails) {
				closeModal();
			}
		},
		[closeOrderDetails, isOrderOpen, showDetails],
	);

	useEffect(() => {
		document.addEventListener('mousedown', clickOutsideDetails);
		return () => {
			document.removeEventListener('mousedown', clickOutsideDetails);
		};
	}, [clickOutsideDetails]);

	useEffect(() => {
		getContractsCallback().then(() => {});
	}, [getContractsCallback]);

	useEffect(() => {
		if (toastScheduledRef.current || loading) return;

		const shouldToast =
			sessionStorage.getItem('contractCreated') ||
			(location.state && location.state.contractCreated);

		if (!shouldToast) return;

		toastScheduledRef.current = true;

		sessionStorage.removeItem('contractCreated');
		if (location.state?.contractCreated) {
			navigate(location.pathname, { replace: true, state: null });
		}

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				toastVariant('Contract successfully created!', false, undefined, {
					className: 'bg-emerald-600 text-white rounded-xl',
					progressClassName: 'bg-white',
					icon: (
						<div className="w-[24px] h-[24px]">
							<SypacToast width={24} height={24} />
						</div>
					),
					style: {
						minHeight: 'auto',
						padding: 0,
						border: '1px solid #E8E8E8',
						borderRadius: 8,
					},
				});
			});
		});
	}, [loading, location.pathname, location.state, navigate]);

	useEffect(() => {
		const idFromState = (location.state as { openContractId?: number } | null)
			?.openContractId;
		const idFromParam = contractId ? parseInt(contractId, 10) : undefined;
		const targetId = idFromState ?? idFromParam;

		if (!targetId || Number.isNaN(targetId) || loading) return;

		if (hasConsumedOpenFromStateRef.current === location.key) return;
		hasConsumedOpenFromStateRef.current = location.key;

		(async () => {
			const existing = contracts.find((c) => c.id === targetId);
			if (existing) {
				await handleContractClick(existing);
			} else {
				try {
					const { data } = await ContractsService.getContractById(
						targetId,
						'customer',
					);
					setContracts((prev) =>
						prev.find((c) => c.id === targetId) ? prev : [data, ...prev],
					);
					setSelectedContract(data);
					setShowDetails(true);
				} catch (e) {}
			}

			try {
				const s = window.history.state;
				if (s && s.usr && 'openContractId' in s.usr) {
					const nextUsr = { ...s.usr };
					delete nextUsr.openContractId;
					window.history.replaceState({ ...s, usr: nextUsr }, '');
				}
			} catch {}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.key, loading, contracts, handleContractClick, contractId]);

	return (
		<main className="relative flex flex-col gap-5 w-[calc(100vw-67px)] xl-2xl:w-[calc(100vw-83px)] h-full">
			<header className="hidden xl:flex items-center mx-5">
				<SypacText variant="heading-4">
					<p className="text-gray-80 mt-[6px]">
						<T keyName="adminContracts.contracts">Contracts</T>
					</p>
				</SypacText>
			</header>
			<div className="border-0 border-solid border-b-[1px] border-gray-10" />

			<div className="flex items-center mx-5">
				<SypacTabs className="flex w-fit p-[3px] gap-[4px]">
					{[
						{ label: t('orders.all', 'All'), buttonId: 'all', statuses: [] },
						{
							label: t('ordersList.toDo', 'To do'),
							buttonId: 'to_do',
							statuses: [ContractStatus.to_do],
						},
						{
							label: t('ordersList.progress', 'In progress'),
							buttonId: 'progress',
							statuses: [ContractStatus.in_progress],
						},
						{
							label: t('ordersList.completed', 'Completed'),
							buttonId: 'completed',
							statuses: [ContractStatus.completed],
						},
						{
							label: t('ordersList.expired', 'Expired'),
							buttonId: 'expired',
							statuses: [ContractStatus.expired],
						},
						{
							label: t('ordersList.canceled', 'Canceled'),
							buttonId: 'canceled',
							statuses: [ContractStatus.canceled],
						},
						// {
						// 	label: t('ordersList.deleted', 'Deleted'),
						// 	buttonId: 'deleted',
						// 	statuses: ['deleted'],
						// },
					].map(({ label, buttonId, statuses }) => (
						<TabsButton
							key={buttonId}
							label={label}
							buttonId={buttonId}
							onClick={() => setCurrentTab(statuses)}
							isSelected={selectedTab === buttonId}
							handleSelectedTab={() => setSelectedTab(buttonId)}
						/>
					))}
				</SypacTabs>

				<div className="flex gap-[40px] items-center ml-auto">
					<Sorting
						options={sortOptions}
						action={(item) => handleSorting(item)}
						current={sorting}
						label={{ title: 'Sorting filter here:', key: 'sortingFilterHere' }}
						customDefault={{ title: 'All', key: 'all' }}
						forContracts
						contractsDateSectionLabel={{
							title: 'Sort by expiration date',
							keyName: 'adminContracts.sortByExpirationDate',
						}}
					/>

					<SearchBar
						placeholder={t(
							'adminContracts.searchBy',
							'Search by Product name or ID',
						)}
						onChange={setSearchQuery}
						showButton={false}
						classNames="w-[288px]"
					/>
				</div>
			</div>

			<section className="w-full h-[calc(100%-119px)] xl:h-full min-h-0">
				{loading ? (
					<div className="flex w-full h-full items-center justify-center">
						<MutatingDots
							height="100"
							width="100"
							color="#7693F4"
							secondaryColor="#494C83"
							radius="12.5"
							ariaLabel="mutating-dots-loading"
							visible
						/>
					</div>
				) : (
					<div className="h-full relative ml-5 mr-7.5 xl:mx-5 border border-solid border-gray-10 rounded-10 overflow-hidden whitespace-nowrap">
						<div
							className={`w-full overflow-y-auto scroll-smooth pr-[5px] ${
								contracts.length ? 'h-[calc(100%-56px)]' : 'h-[calc(100%+2px)]'
							}`}
							style={{ scrollbarGutter: 'stable' }}
						>
							<ContractsTable
								rows={contracts}
								rowClick={handleContractClick}
								search={searchQuery}
								clickedOutside={!showDetails}
								role="buyer"
								currentTab={selectedTab}
							/>
						</div>

						{contracts.length ? (
							<div className="w-full absolute bottom-0 z-10 border border-solid border-gray-10 border-l-0 border-r-0 border-b-0 shadow-pagination bg-white">
								<div className="flex justify-between items-center h-[56px] px-4">
									<Pagination
										showText={true}
										count={count}
										page={page}
										onClick={setPage}
									/>
								</div>
							</div>
						) : null}
					</div>
				)}
			</section>

			{selectedContract && showDetails && (
				<div
					ref={detailsRef}
					className="fixed inset-y-0 right-0 z-[70] w-[716px] max-w-[90vw] bg-white border-l border-gray-10 shadow-xl"
					role="dialog"
					aria-modal="true"
				>
					<ContractsDetails
						contract={selectedContract}
						role={OrderTarget.customer}
						onOrderClick={openOrderDetails}
						onContractUpdate={handleContractUpdate}
					/>
				</div>
			)}

			{isOrderOpen && selectedOrderDetails?.id && (
				<div
					ref={orderDetailsRef}
					className="fixed inset-y-0 right-0 z-[120] w-[716px] max-w-[90vw]"
				>
					<OrderDetails
						isOpen={true}
						selectedOrder={selectedOrderDetails}
						onBack={closeOrderDetails}
						hideViewContractButton={true}
					/>
				</div>
			)}
		</main>
	);
};

export default BuyerContracts;
